import { useLayoutEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useAppearance } from './AppearanceProvider';

// Reveal once as content comes into view; ambient layers stop off-screen.
export default function ExperienceMotion({ children, enabled = true }) {
  const root = useRef(null), seen = useRef(new WeakSet()), reduced = useReducedMotion();
  const { preferences } = useAppearance(), quiet = preferences.quiet;
  useLayoutEffect(() => {
    const node = root.current;
    if (!enabled || !node) return;
    const watched = new WeakSet(), animations = new Map();
    let stagger = 0, staggerReset;
    const reveal = element => {
      if (seen.current.has(element)) return; seen.current.add(element);
      if (reduced || quiet || !element.animate) return;
      const bar=element.hasAttribute('data-motion-bar');
      // A nested chart never gets a second travelling entrance on top of its card.
      const travellingParent=[...animations.keys()].some(parent=>parent.contains(element));
      const frames=bar ? [{transform:'scaleX(0)'},{transform:'scaleX(1)'}] : travellingParent ? [{opacity:0},{opacity:1}] : [{opacity:0,transform:'translateY(14px)'},{opacity:1,transform:'translateY(0)'}];
      const animation=element.animate(frames, {duration:bar?850:520,delay:Math.min(stagger++*35,140),easing:'cubic-bezier(.16,1,.3,1)',fill:'backwards'});
      if(bar)element.style.transformOrigin='left center';
      animations.set(element,animation); animation.onfinish=()=>animations.delete(element);
      clearTimeout(staggerReset);staggerReset=setTimeout(()=>{stagger=0;},80);
    };
    const observer=typeof IntersectionObserver==='function'?new IntersectionObserver(entries=>{
      for(const entry of entries){
        if(entry.target.matches('.tl-certificate-image,.tl-preview-certificate,.tl-public-example-paper,.tl-paper-lift')) {
          entry.target.dataset.motionVisible=String(entry.isIntersecting);
          const paper=entry.target.closest('.tl-paper-stage');
          if(paper)paper.dataset.motionVisible=String(entry.isIntersecting);
        }
        if(entry.isIntersecting&&!entry.target.matches('.tl-paper-lift'))reveal(entry.target);
      }
    },{threshold:.08}):null;
    const discover=()=>{
      const candidates=node.querySelectorAll('.tl-orbit-hero,.tl-orbit-home-cards>.tl-orbit-surface,.tl-orbit-recent,.tl-orbit-focus,.tl-public-trade,.tl-public-metrics>div,.tl-gallery-trade,.trade-row,.tl-weekly-plan,.tl-daily-plan,.stat-card,.tl-panel,.tl-analytics-stat,.tl-analytics-vista,.tl-wall-certificate,.tl-certificate-image,.tl-preview-certificate,.tl-public-example-paper,.tl-paper-milestone,.tl-paper-lift,[data-motion-bar],.tl-trade-form .form-section,.tl-outlook-editor>fieldset>div,.tl-public-section-heading,.tl-entrance-themes>.tl-theme-choices,.tl-faq details,[data-motion-surface]');
      for(const element of candidates){
        if(watched.has(element))continue;watched.add(element);
        if(observer)observer.observe(element);else if(element.getBoundingClientRect().top<window.innerHeight)reveal(element);
      }
    };
    discover();const mutation=new MutationObserver(discover);mutation.observe(node,{childList:true,subtree:true});
    const visibility=()=>{if(document.hidden){for(const animation of animations.values())animation.cancel();animations.clear();}};
    document.addEventListener('visibilitychange',visibility);
    return()=>{mutation.disconnect();observer?.disconnect();clearTimeout(staggerReset);document.removeEventListener('visibilitychange',visibility);for(const animation of animations.values())animation.cancel();};
  }, [reduced, quiet, enabled]);
  return enabled ? <div ref={root} className="tl-orbit-motion-layer">{children}</div> : children;
}
